import Vue from 'vue';
import VueRouter, { RouteConfig, Route, NavigationGuardNext } from 'vue-router';
import VueGtm from '@gtm-support/vue2-gtm';
import VueGtag from 'vue-gtag';
import {
  createThrottledChecker,
  setupChecker,
} from 'spa-ionata-admin/lib/services/manifestCheck';
import Home from '../views/Home.vue';

Vue.use(VueRouter);

const routes: RouteConfig[] = [
  {
    path: '/',
    name: 'Home',
    component: Home,
  },
  {
    path: '/business/:slug',
    name: 'Business',
    component: () =>
      import(
        /* webpackChunkName: "business-profile" */ '../views/BusinessProfile.vue'
      ),
  },
  {
    path: '/search',
    name: 'Search',
    component: () =>
      import(/* webpackChunkName: "search" */ '../views/SearchResults.vue'),
  },
  // TODO(evo): remove once the template is finished
  {
    path: '/business-profile',
    redirect: '/business/profile',
  },
  {
    path: '/enquiry/:slug',
    name: 'Enquiry',
    component: () =>
      import(/* webpackChunkName: "enquiries" */ '../views/Enquiry.vue'),
  },
  {
    path: '/application-process',
    component: () =>
      import(
        /* webpackChunkName: "application-process" */ '../views/ApplicationProcess.vue'
      ),
    children: [
      {
        path: '',
        name: 'ApplicationProcess',
        beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => {
          next({ name: 'EligibilityForm' });
        },
      },
      {
        path: 'eligibility-form',
        name: 'EligibilityForm',
        component: () =>
          import(
            /* webpackChunkName: "application-process" */ '../views/EligibilityForm.vue'
          ),
      },
    ],
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () =>
      import(/* webpackChunkName: "contact" */ '../views/Contact.vue'),
  },
  {
    path: '/set-password',
    redirect: (to: Route) => {
      const path = `admin${to.fullPath}`;
      // A bit of a hack, but the bloody router redirects by pushing a new state
      window.location.href = `${process.env.BASE_URL}${path}`;
      return { path: `/${path}`, replace: true };
    },
  },
  {
    path: '/mission',
    redirect: '/mission/gulfood2025',
  },
  {
    path: '/mission/:slug',
    name: 'Mission',
    component: () =>
      import(
        /* webpackChunkName: "trade-mission" */ '../views/TradeMission.vue'
      ),
  },
  // Let /admin fall-through to the browser to handle
  { path: '/admin/*' },
  { path: '/admin', redirect: '/admin/' },
  {
    path: '/:slug',
    name: 'Post',
    component: () =>
      import(/* webpackChunkName: "about" */ '../views/Post.vue'),
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scrollBehavior(to: Route, from: Route, savedPosition?: any) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return undefined;
    }
    return { x: 0, y: 0 };
  },
});

setupChecker({ base: '/directory' });
const checkForManifestUpdate = createThrottledChecker(5);

router.beforeEach((to, from, next) => {
  checkForManifestUpdate();
  next();
});

// Google Tag Manager Container
if (process.env.VUE_APP_GTM_ID) {
  Vue.use(VueGtm, {
    id: process.env.VUE_APP_GTM_ID,
    defer: false,
    enabled: true,
    debug: false,
    compatibility: false,
    loadScript: true,
    vueRouter: router,
    trackOnNextTick: false,
  });
}

// Gtag for GA4 streams
const ga4MeasurementIds = process.env.VUE_APP_GA4_MEASUREMENT_IDS;
if (ga4MeasurementIds) {
  try {
    const tagIds = JSON.parse(ga4MeasurementIds) as string[];
    Vue.use(VueGtag, {
      config: {
        id: tagIds.pop(),
        params: { send_page_view: true },
      },
      includes: tagIds.map(id => {
        return {
          id,
          params: { send_page_view: true },
        };
      }),
    });
  } catch (_err) {
    // ignore
  }
}

export default router;
